import { KeyLabelArrays } from 'APP/Lib/ProfileDataFormat'

function toSnakeCase(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

function snakeCaseKeys(obj) {
  if (obj === null || obj === undefined) return obj
  if (typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(snakeCaseKeys)

  return Object.keys(obj).reduce((acc, key) => {
    const snakeKey = toSnakeCase(key)
    acc[snakeKey] = snakeCaseKeys(obj[key])
    return acc
  }, {})
}

const COUNTRY_NAMES = {
  CA: 'Canada',
  US: 'United States',
}

const PROVINCE_LABEL_MAP = KeyLabelArrays.PROVINCES.reduce((acc, province) => {
  if (province?.key) {
    acc[province.key] = province.label || province.key
  }
  return acc
}, {})

function splitStreetComponents(primary, secondary) {
  // Clean primary address string, defaulting to empty string if null/undefined
  const primaryAddress = (primary || '').trim()
  const fallbackValue = (secondary || '').trim()

  if (!primaryAddress) {
    return {
      streetNumber: fallbackValue || undefined,
      route: undefined,
    }
  }

  // Extract street number from address string
  // Pattern matches: digits at start (e.g., "123") + optional letter suffix (e.g., "456A")
  // Examples: "123 Main St" → "123", "456B Elm Rd" → "456B"
  const numberMatch = primaryAddress.match(/^([0-9]+[A-Za-z]?)/)

  if (numberMatch) {
    const streetNumber = numberMatch[1]
    const remainder = primaryAddress.slice(streetNumber.length).trim()
    return {
      streetNumber,
      route: remainder || fallbackValue || undefined,
    }
  }

  if (fallbackValue) {
    return {
      streetNumber: fallbackValue,
      route: primaryAddress,
    }
  }

  return {
    streetNumber: primaryAddress,
    route: undefined,
  }
}

function parseRegionInfo(adminIsoCode) {
  if (!adminIsoCode) {
    return {}
  }

  const [countryCode, regionCode] = adminIsoCode.split('-')
  return {
    countryCode,
    regionCode,
    countryName: COUNTRY_NAMES[countryCode] || countryCode,
    regionName: PROVINCE_LABEL_MAP[regionCode] || regionCode,
  }
}

/**
 * Build addressComponents from individual place fields
 * Maps street_number, locality, admin_area_iso_code, and postal_code to component format
 */
function buildAddressComponents(place) {
  const components = []
  const { streetNumber, route } = splitStreetComponents(place.street_number, place.street_number2)

  if (streetNumber) {
    components.push({
      longName: streetNumber,
      shortName: streetNumber,
      types: ['street_number'],
    })
  }

  if (route) {
    components.push({
      longName: route,
      shortName: route,
      types: ['route'],
    })
  }

  if (place.locality) {
    components.push({
      longName: place.locality,
      shortName: place.locality,
      types: ['locality', 'political'],
    })
  }

  const { countryCode, regionCode, countryName, regionName } = parseRegionInfo(
    place.admin_area_iso_code
  )

  if (regionCode) {
    components.push({
      longName: regionName,
      shortName: regionCode,
      types: ['administrative_area_level_1', 'political'],
    })
  }

  if (place.postal_code) {
    components.push({
      longName: place.postal_code,
      shortName: place.postal_code,
      types: ['postal_code'],
    })
  }

  if (countryCode) {
    components.push({
      longName: countryName,
      shortName: countryCode,
      types: ['country', 'political'],
    })
  }

  return components
}

/**
 * Build formatted address string from individual components
 */
function buildFormattedAddress(place) {
  const parts = []

  if (place.street_number) parts.push(place.street_number)
  if (place.locality) parts.push(place.locality)
  if (place.admin_area_iso_code) parts.push(place.admin_area_iso_code)
  if (place.postal_code) parts.push(place.postal_code)

  // If we have no components, fallback to place name
  return parts.length > 0 ? parts.join(', ') : place.name
}

export function transformAnswer(place) {
  // Build addressComponents from individual fields
  const mappedAddressComponents = buildAddressComponents(place)

  // Use provided address or build one from components
  const formattedAddress = place.address || buildFormattedAddress(place)

  const addressComponents =
    place.address_components || place.addressComponents || mappedAddressComponents

  const location = place.coordinates || place.location
  const addressTypes = place.types || place.address_types || place.addressTypes
  const addressWebsite = place.website || place.address_website || place.addressWebsite
  const phoneNumber = place.phone_number || place.phoneNumber
  const placeId = place.placeId || place.place_id || place.placeID || place.external_id

  const formattedPlace = {
    ...place,
    address: formattedAddress,
    addressTypes,
    addressWebsite,
    addressComponents,
    placeId,
    phoneNumber,
    location,
  }

  delete formattedPlace.types
  delete formattedPlace.website
  delete formattedPlace.external_id
  delete formattedPlace.coordinates
  delete formattedPlace.phone_number
  delete formattedPlace.priceLevel

  return snakeCaseKeys(formattedPlace)
}

export function normalizeAddressPayload(payload) {
  const normalizedAddressComponents = (
    payload.addressComponents ||
    payload.address_components ||
    []
  ).map((component) => ({
    long_name: component.long_name || component.longName || component.name,
    short_name: component.short_name || component.shortName,
    types: component.types,
  }))
  const placeIdValue = payload.placeID ?? payload.placeId ?? payload.place_id

  const newPayload = {
    ...payload,
    addressComponents: normalizedAddressComponents,
    placeId: placeIdValue,
  }
  delete newPayload.placeID

  const snakeCasePayload = snakeCaseKeys(newPayload)

  // We do JSON.stringify two times because Rasa needs a JSON string of a JSON object.
  return JSON.stringify(JSON.stringify(snakeCasePayload))
}

export function getSelectedAddressPayload(place, place_type, payload_template) {
  const addressInfo = {
    address: place.address,
    name: place.name,
    openingHours: place.openingHours,
    phoneNumber: place.phoneNumber,
    placeType: place_type,
    placeId: place.placeID ?? place.placeId,
  }

  const stringifiedPayload = normalizeAddressPayload(place)

  const selectedAddress = {
    payloadTemplate: payload_template,
    addressInfo: addressInfo,
    payload: stringifiedPayload,
  }

  return selectedAddress
}
